// The /device approve page (Erwin 2026-10-29 O1/O3; Nexus Docs/DESIGN_WEB_HANDOFF_STEPUP_2026-10-29.md, NEXUS-1436).
//
// /device?code=XXXX-XXXX[&return=<uri>] approves a device code with a fresh passkey step-up:
//  - purpose 'handoff_stepup': a Helm XR web window asks this same person to confirm it's them (O1 from a phone/Mac;
//    O3 from Chrome on the Beam Pro, which then returns to Helm XR). The gateway may echo `returnUri` (only a value on
//    its own allow-list) and ONLY that echoed value is ever navigated to — never the raw query parameter.
//  - purpose 'sign_in' (or absent): an ordinary device sign-in (e.g. the glasses' own). Never redirects, and the code
//    is NEVER taken from the link (M1: a phisher could send a link for their own device's code): the person types the
//    code the device shows them. The device name and user agent are the device's own claims — shown quoted, as data.
//
// /approve is an alias of /device (Erwin 2026-10-29 12:12, CONTRACT_O1_NUMBER_MATCH): nobody can scan a QR shown inside
// the XREAL glasses, so the glasses show only a 2-digit match number and this page lists the pending hand-off step-ups
// of the signed-in person (GET /auth/device/stepups, newest first). "Only approve if your glasses show NN."

import type { AuthCaller, AuthReply } from './authCall';
import { codeOfReply } from './authCall';

/** 'abcd efgh' / 'ABCD-EFGH' -> 'ABCD-EFGH'; null unless it is 8 letters/digits. */
export function normalizeUserCode(raw: string | null | undefined): string | null {
  const s = (raw ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  return s.length === 8 ? `${s.slice(0, 4)}-${s.slice(4)}` : null;
}

export interface DeviceQuery { code: string | null; returnUri: string | null }
export function parseDeviceQuery(search: string): DeviceQuery {
  const q = new URLSearchParams(search);
  const r = q.get('return');
  return { code: normalizeUserCode(q.get('code')), returnUri: r && r.trim() ? r.trim() : null };
}

export type DevicePurpose = 'sign_in' | 'handoff_stepup';
export interface DeviceInfo {
  userCode: string;
  purpose: DevicePurpose;
  appId: string | null;
  deviceName: string | null;
  deviceUserAgent: string | null;
  createdAt: string | null;
  status: string;
  expiresAt: string | null;
  targetOrigin: string | null;
  vouchesHeadset: boolean;
  /** 10..99 for a hand-off step-up (the number the glasses show); null for sign_in codes / older gateways. */
  matchNumber: number | null;
  grants: { passkey: boolean; admin: boolean; until: string | null };
}

/** A match number is a whole number 10..99; anything else (missing, older gateway, garbage) is null. */
export function parseMatchNumber(v: unknown): number | null {
  return typeof v === 'number' && Number.isInteger(v) && v >= 10 && v <= 99 ? v : null;
}

/** The sentence next to the big number. Null when there is no number to compare. */
export function matchLine(n: number | null): string | null {
  return n === null ? null : `Only approve if your glasses show ${n}`;
}

export function parseDeviceInfo(b: Record<string, unknown>): DeviceInfo | null {
  if (typeof b.userCode !== 'string') return null;
  const g = (b.grants && typeof b.grants === 'object' ? b.grants : {}) as Record<string, unknown>;
  return {
    userCode: b.userCode,
    purpose: b.purpose === 'handoff_stepup' ? 'handoff_stepup' : 'sign_in',
    appId: typeof b.appId === 'string' ? b.appId : null,
    deviceName: typeof b.deviceName === 'string' && b.deviceName ? b.deviceName : null,
    deviceUserAgent: typeof b.deviceUserAgent === 'string' && b.deviceUserAgent ? b.deviceUserAgent : null,
    createdAt: typeof b.createdAt === 'string' ? b.createdAt : null,
    status: typeof b.status === 'string' ? b.status : 'pending',
    expiresAt: typeof b.expiresAt === 'string' ? b.expiresAt : null,
    targetOrigin: typeof b.targetOrigin === 'string' && b.targetOrigin ? b.targetOrigin : null,
    vouchesHeadset: b.vouchesHeadset === true,
    matchNumber: b.purpose === 'handoff_stepup' ? parseMatchNumber(b.matchNumber) : null,
    grants: { passkey: g.passkey === true, admin: g.admin === true, until: typeof g.until === 'string' ? g.until : null },
  };
}

export interface DeviceSummary { title: string; warning: string | null; lines: string[] }

/** M1: a code from the link is used as-is only for a hand-off step-up (the gateway binds it to this account). */
export function acceptsCodeFromLink(i: Pick<DeviceInfo, 'purpose'>): boolean {
  return i.purpose === 'handoff_stepup';
}

/** Untrusted text (the device's own claims) as a quoted, length-capped value — never part of a sentence. */
export function quoted(v: string, max = 120): string {
  const t = v.replace(/[\u0000-\u001f\u007f]+/g, ' ').trim();
  return `“${t.length > max ? `${t.slice(0, max)}…` : t}”`;
}

export const SIGN_IN_WARNING = 'Only approve a device you are holding right now. If someone sent you this code or a link to it, deny it.';

/** What the page says is asking. Our own sentences only; device-supplied values go into labelled, quoted lines. */
export function describeDevice(i: DeviceInfo): DeviceSummary {
  const lines: string[] = [];
  let title: string;
  let warning: string | null = null;
  if (i.purpose === 'handoff_stepup') {
    title = `Helm XR web window on ${i.targetOrigin ?? 'an unknown site'} asks you to confirm it's you`;
    if (i.vouchesHeadset) lines.push('Approving also lets this headset count as your passkey in Helm on the glasses (never for admin).');
  } else {
    title = `A device asks to sign in${i.appId ? ` to ${i.appId}` : ''}`;
    warning = SIGN_IN_WARNING;
    if (i.grants.passkey) lines.push(`It will count as passkey-confirmed${i.grants.admin ? ' (including admin)' : ''}.`);
  }
  if (i.deviceName) lines.push(`Device name: ${quoted(i.deviceName, 80)}`);
  if (i.deviceUserAgent) lines.push(`Device software: ${quoted(i.deviceUserAgent)}`);
  if (i.createdAt) lines.push(`Asked at: ${new Date(i.createdAt).toLocaleString()}`);
  if (i.grants.until) lines.push(`The confirmation lasts until ${new Date(i.grants.until).toLocaleString()}.`);
  return { title, warning, lines };
}

export function deviceErrorMessage(r: AuthReply): string {
  switch (codeOfReply(r)) {
    case 'WRONG_ACCOUNT': return 'This code belongs to another account. Sign in here as the person who is asking.';
    case 'PASSKEY_REQUIRED': return 'Your passkey confirmation did not count — try again.';
    case 'ADMIN_MFA_REQUIRED': return 'Confirm with your passkey, then approve.';
    case 'HANDOFF_CANNOT_APPROVE': return 'A Helm XR web window cannot approve codes. Approve on your phone or Mac.';
    case 'RETURN_URI_NOT_ALLOWED': return 'The return link in this address is not allowed. You can approve without returning, then switch back to Helm XR yourself.';
    case 'INVALID_CODE': return 'This code is not valid or has expired.';
    case 'EXPIRED': return 'This code has expired.';
    case 'ALREADY_USED': return 'This code was already used.';
    case 'ACCESS_DENIED': return 'This code was already declined.';
    default: return typeof r.body.message === 'string' && r.body.message ? r.body.message : `Something went wrong (HTTP ${r.status}).`;
  }
}

/** Only a returnUri the SERVER echoed, only for a hand-off step-up code. Never the query parameter. */
export function redirectTarget(purpose: DevicePurpose, approveBody: Record<string, unknown>): string | null {
  if (purpose !== 'handoff_stepup') return null;
  const r = approveBody.returnUri;
  return typeof r === 'string' && r ? r : null;
}

export type LoadResult = { ok: true; info: DeviceInfo } | { ok: false; message: string };
export async function loadDeviceInfo(call: AuthCaller, userCode: string): Promise<LoadResult> {
  let r: AuthReply;
  try { r = await call('GET', `device/info?userCode=${encodeURIComponent(userCode)}`); } catch { return { ok: false, message: 'Cannot reach the sign-in service right now — try again in a moment.' }; }
  if (r.status === 200) { const info = parseDeviceInfo(r.body); if (info) return { ok: true, info }; }
  if (r.status === 404) return { ok: false, message: deviceErrorMessage({ status: 404, body: { code: 'INVALID_CODE' } }) };
  return { ok: false, message: deviceErrorMessage(r) };
}

export type ApproveResult = { ok: true; redirect: string | null } | { ok: false; message: string; code?: string };

/**
 * Approve: a fresh passkey step-up FIRST (the gateway wants the approver passkey-confirmed now), then
 * POST /auth/device/approve {userCode, returnUri?}. `returnUri` is sent only for hand-off step-up codes.
 */
export async function approveDevice(
  info: Pick<DeviceInfo, 'userCode' | 'purpose'>,
  returnUri: string | null,
  deps: { passkey: () => Promise<void>; call: AuthCaller },
): Promise<ApproveResult> {
  try { await deps.passkey(); } catch (e) { return { ok: false, message: e instanceof Error ? e.message : String(e), code: 'PASSKEY_CANCELLED' }; }
  let r: AuthReply;
  try {
    r = await deps.call('POST', 'device/approve', { userCode: info.userCode, ...(info.purpose === 'handoff_stepup' && returnUri ? { returnUri } : {}) });
  } catch { return { ok: false, message: 'Cannot reach the sign-in service right now — try again in a moment.' }; }
  if (r.status === 200) return { ok: true, redirect: redirectTarget(info.purpose, r.body) };
  return { ok: false, message: deviceErrorMessage(r), code: codeOfReply(r) };
}

export async function denyDevice(userCode: string, call: AuthCaller): Promise<{ ok: boolean; message?: string }> {
  try {
    const r = await call('POST', 'device/deny', { userCode });
    return r.status === 200 ? { ok: true } : { ok: false, message: deviceErrorMessage(r) };
  } catch { return { ok: false, message: 'Cannot reach the sign-in service right now — try again in a moment.' }; }
}

// ---- "Approve sign-ins": pending hand-off step-ups of this person (GET /auth/device/stepups) ----

export interface PendingStepUp {
  userCode: string;
  matchNumber: number | null;
  targetOrigin: string | null;
  deviceName: string | null;
  createdAt: string | null;
  expiresAt: string | null;
  vouchesHeadset: boolean;
}

export function parseStepUps(body: Record<string, unknown>): PendingStepUp[] {
  const list = Array.isArray(body.stepups) ? body.stepups : [];
  const out: PendingStepUp[] = [];
  for (const raw of list) {
    if (!raw || typeof raw !== 'object') continue;
    const b = raw as Record<string, unknown>;
    const code = normalizeUserCode(typeof b.userCode === 'string' ? b.userCode : null);
    if (!code) continue;
    out.push({
      userCode: code,
      matchNumber: parseMatchNumber(b.matchNumber),
      targetOrigin: typeof b.targetOrigin === 'string' && b.targetOrigin ? b.targetOrigin : null,
      deviceName: typeof b.deviceName === 'string' && b.deviceName ? b.deviceName : null,
      createdAt: typeof b.createdAt === 'string' ? b.createdAt : null,
      expiresAt: typeof b.expiresAt === 'string' ? b.expiresAt : null,
      vouchesHeadset: b.vouchesHeadset === true,
    });
  }
  return out;
}

/**
 * - ok: the list (possibly empty)
 * - unsupported: the gateway is older than the endpoint (404) or this session may not list (403, e.g. a hand-off
 *   window) — the page just hides the section and keeps the typeable code
 * - error: a transient problem; the poll tries again
 */
export type StepUpsResult =
  | { k: 'ok'; items: PendingStepUp[] }
  | { k: 'unsupported' }
  | { k: 'error'; message: string };

export async function loadStepUps(call: AuthCaller): Promise<StepUpsResult> {
  let r: AuthReply;
  try { r = await call('GET', 'device/stepups'); } catch { return { k: 'error', message: 'Cannot reach the sign-in service right now — trying again.' }; }
  if (r.status === 200) return { k: 'ok', items: parseStepUps(r.body) };
  if (r.status === 404 || r.status === 405 || r.status === 403) return { k: 'unsupported' };
  return { k: 'error', message: deviceErrorMessage(r) };
}

/** Drop rows whose expiry has passed (the list is polled every 5 s; the clock ticks in between). */
export function liveStepUps(items: PendingStepUp[], nowMs: number): PendingStepUp[] {
  return items.filter((i) => !i.expiresAt || Date.parse(i.expiresAt) > nowMs);
}

/** "4:05 left" / "expired"; null without an expiry. */
export function timeLeft(expiresAt: string | null, nowMs: number): string | null {
  if (!expiresAt) return null;
  const ms = Date.parse(expiresAt) - nowMs;
  if (!Number.isFinite(ms)) return null;
  if (ms <= 0) return 'expired';
  const s = Math.ceil(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')} left`;
}

/** "XREAL glasses — helm-dev.kstack.app" (the origin is the gateway's record of the asking web window). */
export function stepUpLabel(i: Pick<PendingStepUp, 'targetOrigin'>): string {
  let where = 'an unknown site';
  if (i.targetOrigin) { try { where = new URL(i.targetOrigin).host || quoted(i.targetOrigin, 80); } catch { where = quoted(i.targetOrigin, 80); } }
  return `XREAL glasses — ${where}`;
}

/** Approve a listed step-up: passkey first, then device/approve {userCode} (no return link — nothing to return to). */
export function approveStepUp(i: Pick<PendingStepUp, 'userCode'>, deps: { passkey: () => Promise<void>; call: AuthCaller }): Promise<ApproveResult> {
  return approveDevice({ userCode: i.userCode, purpose: 'handoff_stepup' }, null, deps);
}
